import { defaultTokensFor, type Tokens } from './tokens'

interface MuiColorSlot {
  main?: string
  light?: string
  dark?: string
  contrastText?: string
}

/** Mirrors cinnamon/theme.go — the shape already persisted in Mongo. */
export interface MuiPalette {
  mode?: string
  primary?: MuiColorSlot
  secondary?: MuiColorSlot
  error?: MuiColorSlot
  warning?: MuiColorSlot
  info?: MuiColorSlot
  success?: MuiColorSlot
  background?: { paper?: string; default?: string }
  text?: { primary?: string; secondary?: string; disabled?: string }
}

/**
 * An empty string counts as absent: that is how a cleared field persists, and
 * `''` as a colour would render transparent rather than fall back.
 */
const pick = (value: string | undefined, fallback: string): string =>
  value != null && value !== '' ? value : fallback

/**
 * @param modeOverride the mode the storefront authored, when it authored one.
 *   It has to be known here rather than applied afterwards: the mode decides
 *   which baseline the missing slots fall back to, so a mode set later would
 *   leave light-mode text on a dark surface.
 */
export const fromMuiPalette = (
  palette: MuiPalette | undefined,
  modeOverride?: Tokens['mode'],
): Tokens => {
  const p = palette ?? {}
  const mode = modeOverride ?? (p.mode === 'dark' ? 'dark' : 'light')
  const d = defaultTokensFor(mode)

  return {
    ...d,
    mode,
    color: {
      primary: pick(p.primary?.main, d.color.primary),
      primaryContrast: pick(p.primary?.contrastText, d.color.primaryContrast),
      secondary: pick(p.secondary?.main, d.color.secondary),
      secondaryContrast: pick(p.secondary?.contrastText, d.color.secondaryContrast),
      error: pick(p.error?.main, d.color.error),
      warning: pick(p.warning?.main, d.color.warning),
      info: pick(p.info?.main, d.color.info),
      success: pick(p.success?.main, d.color.success),
      surface: pick(p.background?.paper, d.color.surface),
      background: pick(p.background?.default, d.color.background),
      textPrimary: pick(p.text?.primary, d.color.textPrimary),
      textSecondary: pick(p.text?.secondary, d.color.textSecondary),
      textDisabled: pick(p.text?.disabled, d.color.textDisabled),
    },
  }
}
